import { motion } from "framer-motion";
import { ArrowRight, Instagram, Twitter, Linkedin, MessageSquare, TrendingUp, Sparkles } from "lucide-react";
import SEO from "../components/SEO";

export default function SocialMedia() {
    return (
        <div className="min-h-screen bg-base text-primary">
            <SEO
                title="Best Social Media Management Tool | AI Content Scheduler & Analytics"
                description="blinqbooks is the best social media management tool in 2026. AI content generation, multi-platform scheduling, engagement analytics & brand growth — all in one dashboard. Start free."
            />

            {/* Hero Section */}
            <section className="py-20 px-4 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 text-pink-600 dark:text-pink-400 text-xs font-bold uppercase mb-6">
                            <TrendingUp className="w-4 h-4" />
                            Viral Marketing AI
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 text-primary dark:text-white leading-tight">
                            Growth on <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">Autopilot</span>
                        </h1>
                        <p className="text-lg text-secondary dark:text-gray-300 mb-8 max-w-lg leading-relaxed">
                            Generate month's worth of content in seconds. Post across every platform and track your viral potential with AI-driven insights.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button className="btn-primary w-full sm:w-auto px-8 py-4 shadow-lg text-lg">
                                Accelerate Growth
                            </button>
                            <button className="btn-secondary w-full sm:w-auto px-8 py-4 shadow-sm text-lg">
                                See Analytics
                            </button>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-pink-500/10 blur-[100px] rounded-full"></div>
                        <div className="relative grid grid-cols-2 gap-4">
                            {[
                                { icon: <Instagram className="text-pink-500" />, label: "Instagram", stat: "12k" },
                                { icon: <Twitter className="text-blue-400" />, label: "Twitter", stat: "4k" },
                                { icon: <Linkedin className="text-blue-700" />, label: "LinkedIn", stat: "8k" },
                                { icon: <MessageSquare className="text-emerald-500" />, label: "Active Subs", stat: "15k" },
                            ].map((item, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl p-6 rounded-2xl text-center"
                                >
                                    <div className="mb-2 flex justify-center">{item.icon}</div>
                                    <div className="text-2xl font-bold dark:text-white">{item.stat}</div>
                                    <div className="text-xs text-secondary dark:text-gray-400 font-medium">{item.label}</div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Core Capabilities */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-5xl font-bold mb-16 text-primary dark:text-white">Smart Brand Management</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        {[
                            { title: "AI Copywriter", desc: "Craft perfectly engaging posts for every platform." },
                            { title: "Smart Scheduler", desc: "Post when your audience is most active automatically." },
                            { title: "Visual Studio", desc: "Design stunning social graphics within the app." },
                        ].map((item, i) => (
                            <div key={i} className="group">
                                <div className="w-16 h-16 bg-pink-500/10 dark:bg-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform transition-transform group-hover:scale-110 group-hover:rotate-3">
                                    <Sparkles className="w-8 h-8 text-pink-500" />
                                </div>
                                <h3 className="text-xl font-bold mb-4 dark:text-white">{item.title}</h3>
                                <p className="text-secondary dark:text-gray-400 leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto bg-rose-600 dark:bg-accent rounded-3xl p-12 text-center text-white relative overflow-hidden">
                    <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -mr-32 -mb-32"></div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">Ready to go viral?</h2>
                    <p className="text-rose-100 mb-8 text-lg relative z-10">Start your smart marketing journey today.</p>
                    <button className="px-10 py-4 bg-white text-rose-600 dark:text-accent rounded-xl font-bold hover:bg-opacity-90 transition-all flex items-center gap-2 mx-auto relative z-10 shadow-xl">
                        Get Started for Free <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
            </section>
        </div>
    );
}
